import { z } from "zod";
import { isValidTimeZone } from "../../utils/is-valid-time-zone.js";
import { emailSchema } from "../common/index.js";
import { MeetingAudience, MeetingFormat, MeetingType, MeetingVisibility } from "../enum.js";

export const createMeetingSchema = z
	.object({
		type: z.enum(MeetingType),
		title: z
			.string()
			.min(2, { message: "Title must be at least 2 characters" })
			.max(200, { message: "Title must be at most 200 characters" }),
		description: z.string().max(10000).optional(),
		cover_image: z.url({ message: "Invalid cover image URL" }).optional(),
		format: z.enum(MeetingFormat),
		start_at: z.iso.datetime({ message: "Start date is required" }),
		end_at: z.iso.datetime({ message: "End date is required" }),
		timezone: z.string().min(1).max(100).refine(isValidTimeZone, { message: "Invalid timezone" }).optional(),
		address: z.string().max(500).optional(),
		latitude: z.number().min(-90).max(90).optional(),
		longitude: z.number().min(-180).max(180).optional(),
		max_capacity: z.number().int().min(1, { message: "Capacity must be at least 1" }).optional(),
		audience: z.enum(MeetingAudience).default(MeetingAudience.ENTIRE),
		group_ids: z.array(z.uuid()).optional(),
		member_ids: z.array(z.uuid()).optional(),
		visibility: z.enum(MeetingVisibility).default(MeetingVisibility.PRIVATE),
		external_emails: z.array(emailSchema).max(50).optional(),
	})
	.strict()
	.refine((data) => new Date(data.end_at) > new Date(data.start_at), {
		message: "End time must be after start time",
		path: ["end_at"],
	})
	.refine((data) => data.format === MeetingFormat.ONLINE || data.address, {
		message: "Address is required for in-person or hybrid meetings",
		path: ["address"],
	})
	.refine((data) => data.audience !== MeetingAudience.GROUPS || (data.group_ids && data.group_ids.length > 0), {
		message: "At least one group is required",
		path: ["group_ids"],
	})
	.refine((data) => data.audience !== MeetingAudience.MEMBERS || (data.member_ids && data.member_ids.length > 0), {
		message: "At least one member is required",
		path: ["member_ids"],
	});

export type CreateMeetingPayload = z.infer<typeof createMeetingSchema>;
